// @ts-nocheck
/**
 * Client Portal — Auth Context
 *
 * Completely separate from the tenant AuthContext and AdminAuthContext.
 * Uses its own Cognito pool, JWT tokens, and API endpoints.
 * Mirrors the AdminAuthContext pattern.
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { portalApi } from '@/api/portalClient'
import { useQueryClient } from '@tanstack/react-query'

export interface PortalClient {
  id: string
  userId: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  avatar?: string
}

interface ClientPortalAuthContextValue {
  client: PortalClient | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
}

const ClientPortalAuthContext = createContext<ClientPortalAuthContextValue | null>(null)

export function ClientPortalAuthProvider({ children }: { children: ReactNode }) {
  const [client, setClient] = useState<PortalClient | null>(null)
  const [loading, setLoading] = useState(true)
  const queryClient = useQueryClient()

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      const token = portalApi.auth.getToken()
      if (token) {
        const data = await portalApi.auth.me()
        setClient(data.client)
      }
    } catch {
      portalApi.auth.clearToken()
    } finally {
      setLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    const data = await portalApi.auth.login(email, password)
    portalApi.auth.setToken(data.token)
    setClient(data.client)
  }

  const logout = () => {
    portalApi.auth.clearToken()
    setClient(null)
    queryClient.clear()
  }

  return (
    <ClientPortalAuthContext.Provider value={{ client, loading, login, logout }}>
      {children}
    </ClientPortalAuthContext.Provider>
  )
}

export function usePortalAuth() {
  const ctx = useContext(ClientPortalAuthContext)
  if (!ctx) throw new Error('usePortalAuth must be used within ClientPortalAuthProvider')
  return ctx
}
